import React, { useContext, useState, useCallback, useRef } from 'react';
import { Grid, MenuItem, TextField } from '@material-ui/core';
import { css } from '@emotion/react';
import { compose } from 'recompose';
import { colors } from '@/styles';
import { useClientUser } from '@/hooks';
import { Context, ContextProvider } from './store';
import { PageTitle } from './blocks';
import SubregionList from './SubregionList';
import ZipList from './ZipList';
import AssignToSubregionPopover from './AssignToSubregionPopover';
import Snackbar from '../Snackbar';
import { OnwardSwitch } from '@/styles/blocks';
import SubregionMap from './SubregionMap';

const SubRegions = () => {
    const mapRef = useRef(null);
    const [root, setRoot] = useState(null);
    const { locations } = useClientUser();
    const { callbacks, state, loading } = useContext(Context);
    const [highlightOverride, setHighlightOverride] = useState(false);

    const initRoot = useCallback((node) => {
        if (node !== null) {
            setRoot(node);
        }
    }, []);

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                background: ${colors.white.primary};
                flex-wrap: nowrap;
            `}
        >
            <Snackbar
                open={!!state.notification.message}
                {...state.notification}
                handleClose={() => callbacks.setNotification({})}
            />
            <Grid
                container
                direction="row"
                css={css`
                    padding: 23px 45px;
                    border-bottom: 1px solid ${colors.greys[4]};
                    flex: 0;
                    flex-basis: 0;
                    align-items: center;
                    justify-content: space-between;
                `}
            >
                <PageTitle>Regions</PageTitle>
                <OnwardSwitch
                    label={'Highlight All'}
                    checked={highlightOverride}
                    onChange={() => setHighlightOverride((prev) => !prev)}
                />
            </Grid>

            <Grid
                container
                direction="row"
                css={css`
                    flex-basis: 0;
                    flex: 1;
                    flex-wrap: nowrap;
                `}
            >
                <Grid
                    item
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                        flex-basis: 0;
                        flex-wrap: nowrap;
                        min-width: 320px;
                        border-right: 1px solid ${colors.greys[0]};
                    `}
                >
                    <SubregionList
                        options={{
                            enableAddSubregions: true,
                            enableImport: true,
                            enableExport: true,
                        }}
                        loading={loading}
                        filter={state.filter}
                        selected={state.selectedSubregion}
                        subregions={state.subregions}
                        callbacks={callbacks}
                    />
                </Grid>
                {state.selectedSubregion && (
                    <Grid
                        item
                        container
                        direction="column"
                        css={css`
                            flex: 0;
                            flex-basis: 0;
                            flex-wrap: nowrap;
                            min-width: 320px;
                            border-right: 1px solid ${colors.greys[0]};
                        `}
                    >
                        <Grid
                            item
                            css={css`
                                padding: 18px 20px 0px 20px;
                            `}
                        >
                            <OnwardSwitch
                                label={'Resource Zone'}
                                checked={!!state.selectedSubregion.is_resource_zone}
                                onChange={(_, checked) =>
                                    callbacks.updateSubregion({
                                        subregion_id: state.selectedSubregion.subregion_id,
                                        is_resource_zone: checked,
                                    })
                                }
                                disabled={loading.subregions}
                            />
                        </Grid>
                        <Grid
                            item
                            css={css`
                                padding: 18px 20px 0px 20px;
                            `}
                        >
                            <OnwardSwitch
                                label={'Pricing Zone'}
                                checked={!!state.selectedSubregion.is_pricing_zone}
                                onChange={(_, checked) =>
                                    callbacks.updateSubregion({
                                        subregion_id: state.selectedSubregion.subregion_id,
                                        is_pricing_zone: checked,
                                    })
                                }
                                disabled={loading.subregions}
                            />
                        </Grid>
                        <Grid
                            item
                            css={css`
                                padding: 18px 20px 0px 20px;
                            `}
                        >
                            <TextField
                                select
                                fullWidth
                                size="small"
                                variant="outlined"
                                label="Receiving Warehouse"
                                value={state.selectedSubregion.warehouse_id || 'DEFAULT'}
                                disabled={loading.subregions}
                                onChange={(e) => {
                                    const warehouse_id = e.target.value === 'DEFAULT' ? null : e.target.value;
                                    callbacks.updateSubregion({
                                        subregion_id: state.selectedSubregion.subregion_id,
                                        warehouse_id,
                                    });
                                }}
                            >
                                <MenuItem key="DEFAULT" value={'DEFAULT'}>
                                    Default (Preferred Inbound)
                                </MenuItem>
                                {(locations || []).map((location, i) => (
                                    <MenuItem key={location.location_id} value={location.location_id}>
                                        {location.location_name || `Warehouse ${i + 1}`}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <ZipList
                            options={{}}
                            loading={loading}
                            filter={state.filter}
                            subregion={state.selectedSubregion}
                            zips={state.selectedSubregion.zips}
                            callbacks={callbacks}
                        />
                    </Grid>
                )}
                <Grid
                    item
                    container
                    direction="column"
                    css={css`
                        flex: 1;
                        flex-basis: 0;
                        overflow: hidden;
                        position: relative;
                    `}
                >
                    <div
                        ref={initRoot}
                        css={css`
                            width: 100%;
                            height: 100%;
                            display: flex;
                        `}
                    />
                    <SubregionMap
                        ref={mapRef}
                        node={root}
                        subregions={state.subregions}
                        callbacks={{
                            ...callbacks,
                            onLoad: (map) => {
                                const [long, lat] = locations.reduce((acc, location) => {
                                    if (location.lng && location.lat) {
                                        acc = [location.lng, location.lat];
                                    }

                                    return acc;
                                }, []);

                                if (long && lat) {
                                    map.jumpTo({
                                        center: [long, lat],
                                    });
                                }
                            },
                        }}
                        highlightOverride={highlightOverride}
                        selectedSubregion={state.selectedSubregion}
                        hoveredSubregion={state.hoveredSubregion}
                        selectedZips={state.selectedZips}
                        hoveredSubregionZip={state.hoveredSubregionZip}
                    />

                    {state.selectedZips.length > 0 ? (
                        <AssignToSubregionPopover
                            loading={loading}
                            subregions={state.subregions}
                            selectedZips={state.selectedZips}
                            callbacks={callbacks}
                        />
                    ) : null}
                </Grid>
            </Grid>
        </Grid>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );

export default compose(withContext)(SubRegions);
